import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { axiomTheme } from '../../styles/designTokens';
import Loan360Drawer, { type DrawerTabKey } from './Loan360Drawer';
import { asArray, normalizeLoans } from './CreditOperatingSystemDashboard';
import { AskAxiomPanel, loanCopilotContext } from '../../features/abf/copilot/AbfCopilotLayout';
import { loan360Path, loanListPath, type LoanSource } from '../../utils/loanNavigation';

const Shell = styled.div`
  padding: ${axiomTheme.spacing.page};
  color: ${axiomTheme.color.text.secondary};
`;

const Notice = styled.section`
  padding: 1.25rem 1.5rem;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.55);
  color: #e2e8f0;
  h2 { margin: 0.75rem 0 0.35rem; font-size: 1.35rem; font-weight: 600; }
  p { margin: 0 0 0.75rem; color: #94a3b8; font-size: 0.9rem; }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0;
  border: none;
  background: none;
  color: #cbd5e1;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;

const RetryButton = styled.button`
  border: 1px solid #475569;
  background: transparent;
  color: #bae6fd;
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font: inherit;
  cursor: pointer;
`;

const TABS: DrawerTabKey[] = ['overview', 'agreement', 'servicing', 'underwriting', 'covenants', 'lme', 'workout', 'exceptions', 'documents', 'activity', 'settlement'];
const PRIVATE_CREDIT_ONLY: DrawerTabKey[] = ['agreement', 'servicing', 'documents', 'activity'];

/** The desk sections Loan 360 links to ("Open Covenant Center", the workout module). */
const deskSectionPath = (section: string, context?: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams({ tab: 'credit-loans', sub: section });
  Object.entries(context || {}).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value));
  });
  return `/portfolio-management?${params.toString()}`;
};

type Surveillance = { covenantAlerts: any[]; watchlist: any[]; distressed: any[]; trades: any[] };
const EMPTY: Surveillance = { covenantAlerts: [], watchlist: [], distressed: [], trades: [] };

/**
 * Loan 360 as its own page, like an ABF facility: /portfolio-management/credit/loans/{lane}/{id}?tab=….
 * It reads this loan's record and the records about it (its trades by security, the covenant alerts,
 * watchlist and distressed entries for its borrower); the loan-ops workspace, covenants included, is read by
 * Loan 360 itself. Nothing waits for the loan book or the credit desk.
 */
const Loan360Page: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const source = String(params.lane || '').toUpperCase() as LoanSource;
  const loanId = String(params.loanId || '');
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = (searchParams.get('tab') || 'overview') as DrawerTabKey;
  const tab: DrawerTabKey = TABS.includes(requestedTab) && (requestedTab !== 'settlement' || source === 'LEVERAGED_LOAN')
    && (!PRIVATE_CREDIT_ONLY.includes(requestedTab) || source === 'PRIVATE_CREDIT')
    ? requestedTab : 'overview';
  const validLane = source === 'PRIVATE_CREDIT' || source === 'LEVERAGED_LOAN';

  const [loan, setLoan] = useState<any | null>(null);
  const [loanState, setLoanState] = useState<'loading' | 'ready' | 'missing' | 'failed'>('loading');
  const [slow, setSlow] = useState(false);
  const [surveillance, setSurveillance] = useState<Surveillance>(EMPTY);
  const generation = useRef(0);

  const loadLoan = useCallback(async () => {
    if (!validLane || !loanId) {
      setLoanState('missing');
      return;
    }
    const token = ++generation.current;
    setLoanState(current => current === 'ready' ? current : 'loading');
    try {
      const url = source === 'LEVERAGED_LOAN'
        ? `/api/credit/loans/${encodeURIComponent(loanId)}`
        // The list's own row for this loan, IBOR balances included.
        : `/api/private-credit/loans/${encodeURIComponent(loanId)}/summary`;
      const { data } = await axios.get(url);
      if (token !== generation.current) return;
      const [row] = source === 'LEVERAGED_LOAN' ? normalizeLoans([data], []) : normalizeLoans([], [data]);
      setLoan(row ?? null);
      setLoanState(row ? 'ready' : 'missing');
    } catch (error: any) {
      if (token !== generation.current) return;
      setLoanState(error?.response?.status === 404 ? 'missing' : 'failed');
    }
  }, [loanId, source, validLane]);

  // Records about the loan that Loan 360 matches to it (alerts, watchlist, distressed by borrower; trades by
  // security). Each read stands alone: one failing leaves the others on screen.
  const loadSurveillance = useCallback(async (securityId?: number) => {
    const read = (url: string) => axios.get(url).then(response => asArray<any>(response.data)).catch(() => [] as any[]);
    const [covenantAlerts, watchlist, distressed, trades] = await Promise.all([
      read('/api/credit/covenants/alerts'),
      read('/api/credit/watchlist?limit=200'),
      read('/api/credit/distressed'),
      securityId ? read(`/api/trade-transactions/security/${securityId}?size=500&sort=tradeDate,desc`) : Promise.resolve([] as any[]),
    ]);
    setSurveillance({ covenantAlerts, watchlist, distressed, trades });
  }, []);

  useEffect(() => {
    setLoan(null);
    setSurveillance(EMPTY);
    void loadLoan();
  }, [loadLoan]);

  const securityId: number | undefined = loan?.securityId ?? loan?.security?.id;
  useEffect(() => {
    if (loan) void loadSurveillance(securityId);
  }, [loan, securityId, loadSurveillance]);

  // "Opening…" only when opening takes long enough to notice.
  useEffect(() => {
    setSlow(false);
    if (loanState !== 'loading') return undefined;
    const timer = window.setTimeout(() => setSlow(true), 350);
    return () => window.clearTimeout(timer);
  }, [loanState]);

  const copilotContext = useMemo(
    () => (validLane && loanId ? loanCopilotContext(source, Number(loanId), tab) : null),
    [validLane, loanId, source, tab],
  );

  // Back returns where the user came from; a page opened directly goes to its lane's list.
  const back = () => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate(validLane ? loanListPath(source) : '/portfolio-management?tab=credit-loans');
  };

  const lane = source === 'PRIVATE_CREDIT' ? 'private credit facility' : 'leveraged loan';
  if (loanState !== 'ready' || !loan || !copilotContext) {
    if (loanState === 'loading' && !slow) return <Shell aria-busy="true" />;
    return (
      <Shell>
        <Notice role="status" aria-live="polite">
          <BackLink type="button" onClick={back}><ArrowLeft size={14} /> Back</BackLink>
          {loanState === 'loading' && <><h2>Opening the {lane}…</h2><p>Reading the loan record.</p></>}
          {loanState === 'missing' && <>
            <h2>This loan is not in your book</h2>
            <p>{validLane
              ? `No ${lane} with id ${loanId} is recorded for this tenant. It may have been removed, or the link is for another tenant.`
              : 'This link does not name a private credit facility or a leveraged loan.'}</p>
          </>}
          {loanState === 'failed' && <>
            <h2>The loan could not be read</h2>
            <p>No data was changed. Try again in a moment.</p>
            <RetryButton type="button" onClick={() => void loadLoan()}>Retry</RetryButton>
          </>}
        </Notice>
      </Shell>
    );
  }

  return (
    <AskAxiomPanel context={copilotContext}>
      <Shell>
        <Loan360Drawer
          key={`${loan.source}-${loan.id}`}
          loan={loan}
          // Loan 360 reads the loan's covenants from its own workspace.
          covenants={[]}
          covenantAlerts={surveillance.covenantAlerts}
          watchlist={surveillance.watchlist}
          distressed={surveillance.distressed}
          trades={surveillance.trades}
          defaultTab={tab}
          presentation="page"
          onClose={back}
          onNavigate={(section, context) => navigate(deskSectionPath(section, context))}
          onTabChange={next => {
            const query = new URLSearchParams(searchParams);
            if (next === 'overview') query.delete('tab'); else query.set('tab', next);
            setSearchParams(query, { replace: true });
          }}
          // A fresh loan record also re-reads the records about it.
          onDataChanged={() => { void loadLoan(); }}
        />
      </Shell>
    </AskAxiomPanel>
  );
};

export default Loan360Page;
